import { ValidAny } from "@/types";

/**
 * Validates that a start date comes before an end date
 * @param startDate - The start date (string, null, or undefined)
 * @param endDate - The end date (string, null, or undefined)
 * @param fieldPrefix - The field prefix for error messaging (e.g., "eligibility.sick_days")
 * @param errors - The errors object to populate
 */
export const validateDateRange = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  fieldPrefix: string,
  errors: Record<string, string>
): void => {
  if (startDate && endDate) {
    if (new Date(startDate) > new Date(endDate)) {
      errors[`${fieldPrefix}_end_date`] = "End date cannot be before start date";
    }
  }
};

/**
 * Validates that a date is not in the future
 * @param date - The date to validate
 * @param fieldName - The field name for error messaging
 * @param errors - The errors object to populate
 * @param customMessage - Optional custom error message
 */
export const validateNotFutureDate = (
  date: string | null | undefined,
  fieldName: string,
  errors: Record<string, string>,
  customMessage?: string
): void => {
  if (date) {
    const dateObj = new Date(date);
    const today = new Date();
    if (dateObj > today) {
      errors[fieldName] = customMessage || "Cannot be in the future";
    }
  }
};

/**
 * Validates that a numeric value is greater than or equal to a minimum
 * @param value - The value to validate
 * @param fieldName - The field name for error messaging
 * @param errors - The errors object to populate
 * @param min - The minimum allowed value (default: 0)
 * @param customMessage - Optional custom error message
 */
export const validateMinValue = (
  value: number | null | undefined,
  fieldName: string,
  errors: Record<string, string>,
  min: number = 0,
  customMessage?: string
): void => {
  if (value !== null && value !== undefined && value < min) {
    errors[fieldName] = customMessage || `Must be greater than or equal to ${min}`;
  }
};

/**
 * Validates that a required field is present
 * @param value - The value to validate
 * @param fieldName - The field name for error messaging
 * @param errors - The errors object to populate
 * @param customMessage - Optional custom error message
 */
export const validateRequired = (
  value: ValidAny,
  fieldName: string,
  errors: Record<string, string>,
  customMessage?: string
): void => {
  if (!value && value !== 0) {
    errors[fieldName] = customMessage || "Required";
  }
};

/**
 * Validates age is within reasonable bounds
 * @param age - The age to validate
 * @param fieldName - The field name for error messaging
 * @param errors - The errors object to populate
 * @param minAge - Minimum age (default: 0)
 * @param maxAge - Maximum age (default: 150)
 */
export const validateAge = (
  age: number | null | undefined,
  fieldName: string,
  errors: Record<string, string>,
  minAge: number = 0,
  maxAge: number = 150
): void => {
  if (age !== undefined && age !== null) {
    if (age < minAge || age > maxAge) {
      errors[fieldName] = `Age must be between ${minAge} and ${maxAge}`;
    }
  }
};

/**
 * Validates email format (basic validation)
 * @param email - The email to validate
 * @param fieldName - The field name for error messaging
 * @param errors - The errors object to populate
 */

export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const invalidEmailErrorMessage = "Please enter a valid email address";

export const validateEmail = (
  email: string | null | undefined,
  fieldName: string,
  errors: Record<string, string>
): void => {
  if (email) {
    if (!emailRegex.test(email)) {
      errors[fieldName] = "Please enter a valid email address";
    }
  }
};

/**
 * Validates phone format
 * @param phone - The phone to validate
 * @param fieldName - The field name for error messaging
 * @param errors - The errors object to populate
 */
export const validatePhone = (
  phone: string | null | undefined,
  fieldName: string,
  errors: Record<string, string>
): void => {
  const error = isValidPhone(phone);
  if (error) {
    errors[fieldName] = error;
  }
};

// ============================================
// Pure validators (return error message or empty string)
// ============================================

/**
 * Validates email format
 * @returns Error message or empty string if valid
 */
export const isValidEmail = (email: string | null | undefined): string => {
  if (!email) return "";
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) ? "" : "Please enter a valid email address";
};

/**
 * Validates phone format
 * @returns Error message or empty string if valid
 */
export const isValidPhone = (phone: string | null | undefined): string => {
  if (!phone) return "";
  const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;
  if (!phoneRegex.test(phone)) return "Invalid phone format";
  const digitCount = phone.replace(/\D/g, "").length;
  return digitCount >= 7 ? "" : "Phone number too short";
};

/**
 * Validates US zip code format (5-digit or 5+4)
 * @param zip - The zip code to validate
 * @param fieldName - The field name for error messaging
 * @param errors - The errors object to populate
 */
export const validateZipCodeFormat = (
  zip: string | null | undefined,
  fieldName: string,
  errors: Record<string, string>
): void => {
  if (!zip) return;
  const ZIP_REGEX = /^\d{5}(-\d{4})?$/;
  if (!ZIP_REGEX.test(zip)) {
    errors[fieldName] = "Must be a valid zip code (e.g. 12345 or 12345-6789)";
  }
};
